import {
  ROLE_ADMIN,
  ROLE_PARENT,
  ROLE_PRINCIPAL,
  ROLE_SCHOOL,
  ROLE_STAFF,
  ROLE_STUDENT,
  ROLE_SUPER_ADMIN,
  ROLE_TEACHER,
  ROLE_USER,
  normalizeRole,
} from "./authRole";

const PERMISSION_RULES_KEY = "permissionRules";

const DEFAULT_PERMISSION_RULES = [
  { role: ROLE_ADMIN, module: "Dashboard", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_ADMIN, module: "Admissions", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: true },
  { role: ROLE_ADMIN, module: "Users", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: true },
  { role: ROLE_ADMIN, module: "Students", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_ADMIN, module: "Teachers", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_ADMIN, module: "Classes & Subjects", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_ADMIN, module: "Timetable", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_ADMIN, module: "Exams", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_ADMIN, module: "Fees", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_ADMIN, module: "Notifications", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_ADMIN, module: "Permissions", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: true },
  { role: ROLE_ADMIN, module: "Parent Portal", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Dashboard", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Admissions", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: true },
  { role: ROLE_SCHOOL, module: "Users", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: true },
  { role: ROLE_SCHOOL, module: "Students", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Teachers", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Classes & Subjects", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Timetable", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Exams", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Fees", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Notifications", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_SCHOOL, module: "Parent Portal", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_PRINCIPAL, module: "Dashboard", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_PRINCIPAL, module: "Admissions", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: true },
  { role: ROLE_PRINCIPAL, module: "Students", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_PRINCIPAL, module: "Teachers", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_PRINCIPAL, module: "Timetable", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_PRINCIPAL, module: "Exams", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_PRINCIPAL, module: "Fees", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_TEACHER, module: "Dashboard", canView: true, canCreate: false, canEdit: false, canDelete: false, approvalRequired: false },
  { role: ROLE_TEACHER, module: "Attendance", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_TEACHER, module: "Marks", canView: true, canCreate: true, canEdit: true, canDelete: false, approvalRequired: false },
  { role: ROLE_STUDENT, module: "Dashboard", canView: true, canCreate: false, canEdit: false, canDelete: false, approvalRequired: false },
  { role: ROLE_USER, module: "Dashboard", canView: true, canCreate: false, canEdit: false, canDelete: false, approvalRequired: false },
  { role: ROLE_PARENT, module: "Parent Portal", canView: true, canCreate: false, canEdit: false, canDelete: false, approvalRequired: false },
  { role: ROLE_STAFF, module: "Dashboard", canView: true, canCreate: false, canEdit: false, canDelete: false, approvalRequired: false },
];

const safeJsonParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);
const firstNonEmpty = (...values) => values.find((value) => (
  value !== undefined
  && value !== null
  && (typeof value !== "string" || value.trim() !== "")
));

const toBoolean = (value, fallback = false) => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["false", "0", "no", "denied", "disabled"].includes(normalized)) return false;
    if (["true", "1", "yes", "allowed", "enabled"].includes(normalized)) return true;
  }
  return Boolean(value);
};

const normalizeModuleName = (value) => {
  const moduleName = String(value || "Module").trim();
  const compactName = moduleName.toLowerCase().replace(/[\s_-]+/g, "");

  if (["dashboard", "admindashboard", "maindashboard"].includes(compactName)) {
    return "Dashboard";
  }

  return moduleName || "Module";
};

const isObject = (value) => value && typeof value === "object" && !Array.isArray(value);
const KNOWN_ROLES = new Set([
  ROLE_SUPER_ADMIN,
  ROLE_ADMIN,
  ROLE_SCHOOL,
  ROLE_PRINCIPAL,
  ROLE_TEACHER,
  ROLE_STAFF,
  ROLE_PARENT,
  ROLE_STUDENT,
  ROLE_USER,
]);
const isRoleKey = (value) => KNOWN_ROLES.has(normalizeRole(value));

const getPermissionFlags = (record) => (
  isObject(record.permissions)
    ? record.permissions
    : isObject(record.actions)
      ? record.actions
      : {}
);

export const normalizePermissionRule = (record = {}, index = 0, roleHint = "") => {
  const source = isObject(record) ? record : {};
  const flags = getPermissionFlags(source);
  const roleValue = firstNonEmpty(
    source.role,
    source.userRole,
    source.roleName,
    source.authority,
    Array.isArray(source.roles) ? source.roles[0] : undefined,
    roleHint,
  );
  const moduleValue = firstDefined(
    source.module,
    source.resource,
    source.moduleName,
    source.resourceName,
    source.permissionName,
    source.name,
    "Module",
  );

  return {
    id: source.id || source.permissionId || source._id || `perm-${index + 1}`,
    role: normalizeRole(Array.isArray(roleValue) ? roleValue[0] : roleValue),
    module: normalizeModuleName(moduleValue),
    canView: toBoolean(firstDefined(source.canView, source.view, source.canRead, source.read, flags.canView, flags.view, flags.canRead, flags.read), true),
    canCreate: toBoolean(firstDefined(source.canCreate, source.create, flags.canCreate, flags.create)),
    canEdit: toBoolean(firstDefined(source.canEdit, source.edit, flags.canEdit, flags.edit)),
    canDelete: toBoolean(firstDefined(source.canDelete, source.delete, flags.canDelete, flags.delete)),
    approvalRequired: toBoolean(firstDefined(source.approvalRequired, source.requiresApproval, flags.approvalRequired, flags.requiresApproval)),
  };
};

const looksLikePermissionRule = (value) => isObject(value) && [
  "module",
  "resource",
  "moduleName",
  "resourceName",
  "permissionName",
  "canView",
  "view",
  "canRead",
  "read",
].some((key) => Object.prototype.hasOwnProperty.call(value, key));

const collectPermissionRecords = (value, roleHint = "", records = [], depth = 0) => {
  if (!value || depth > 8) return records;

  if (Array.isArray(value)) {
    value.forEach((item) => collectPermissionRecords(item, roleHint, records, depth + 1));
    return records;
  }

  if (!isObject(value)) return records;

  if (looksLikePermissionRule(value)) {
    records.push({ ...value, role: value.role || roleHint });
    return records;
  }

  const nestedRole = firstDefined(value.role, value.userRole, value.roleName, roleHint, "");
  const wrapperKeys = ["data", "permissions", "permissionRules", "rules", "content", "items", "results"];

  wrapperKeys.forEach((key) => {
    const nested = value[key];
    if (nested === undefined || nested === null) return;

    if (key === "permissions" && isObject(nested) && !Array.isArray(nested)) {
      Object.entries(nested).forEach(([moduleOrRole, moduleRule]) => {
        if (isRoleKey(moduleOrRole) && isObject(moduleRule)) {
          Object.entries(moduleRule).forEach(([moduleName, nestedRule]) => {
            if (isObject(nestedRule)) {
              records.push({ ...nestedRule, role: nestedRule.role || moduleOrRole, module: nestedRule.module || moduleName });
            }
          });
          return;
        }

        if (isObject(moduleRule)) {
          records.push({ ...moduleRule, role: moduleRule.role || nestedRole, module: moduleRule.module || moduleOrRole });
        } else {
          collectPermissionRecords(moduleRule, nestedRole, records, depth + 1);
        }
      });
      return;
    }

    collectPermissionRecords(nested, nestedRole, records, depth + 1);
  });

  // Some APIs return a role-keyed matrix directly:
  // { ROLE_ADMIN: { Dashboard: { view: true } } }.
  if (!wrapperKeys.some((key) => value[key] !== undefined)) {
    Object.entries(value).forEach(([role, modules]) => {
      if (!isObject(modules)) return;
      Object.entries(modules).forEach(([moduleName, moduleRule]) => {
        if (!isObject(moduleRule)) return;
        records.push({ ...moduleRule, role, module: moduleRule.module || moduleName });
      });
    });
  }

  return records;
};

export const extractPermissionRules = (responseData) => (
  collectPermissionRecords(responseData).map(normalizePermissionRule)
);

export const getDefaultPermissionRules = () => DEFAULT_PERMISSION_RULES.map(normalizePermissionRule);

export const getStoredPermissionRules = () => {
  const stored = localStorage.getItem(PERMISSION_RULES_KEY);
  const parsed = stored ? safeJsonParse(stored, []) : [];

  // Browser storage is not an authority source. An absent/corrupt server
  // response must not silently grant the built-in permissions.
  return Array.isArray(parsed) ? parsed.map(normalizePermissionRule) : [];
};

export const persistPermissionRules = (rules = []) => {
  const nextRules = (Array.isArray(rules) && rules.length > 0 ? rules : getDefaultPermissionRules())
    .map(normalizePermissionRule);
  localStorage.setItem(PERMISSION_RULES_KEY, JSON.stringify(nextRules));
  return nextRules;
};

export const syncPermissionRules = (rules = []) => {
  const nextRules = (Array.isArray(rules) && rules.length > 0 ? rules : getDefaultPermissionRules())
    .map(normalizePermissionRule);
  return persistPermissionRules(nextRules);
};

const PERMISSION_FLAG_MAP = {
  view: "canView",
  create: "canCreate",
  edit: "canEdit",
  delete: "canDelete",
};

export const canAccessModule = ({ role, module, action = "view", rules } = {}) => {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) return false;
  if (normalizedRole === ROLE_SUPER_ADMIN) return true;
  if (!module) return true;

  const permissionFlag = PERMISSION_FLAG_MAP[action] || PERMISSION_FLAG_MAP.view;
  const availableRules = Array.isArray(rules) ? rules : getStoredPermissionRules();

  // ROLE_ADMIN is the tenant's management owner. Its base access must not be
  // revoked by a stale, partial, or malformed permission-matrix response in
  // the browser. The server remains the authority for every API operation.
  // Other management roles continue to be governed by the verified matrix.
  if (normalizedRole === ROLE_ADMIN) {
    const defaultMatch = DEFAULT_PERMISSION_RULES.find((item) => (
      item.role === normalizedRole && item.module.toLowerCase() === String(module).toLowerCase()
    ));
    return Boolean(defaultMatch?.[permissionFlag]);
  }

  const match = availableRules.find((item) => (
    item.role === normalizedRole && item.module.toLowerCase() === String(module).toLowerCase()
  ));

  if (!match) return false;
  return Boolean(match[permissionFlag]);
};
